import { inject, Injector } from '@angular/core'

import { BehaviorSubject } from 'rxjs'

import { CustomSensor } from '@ti/contracts'
import { ApiService } from '@ti/core/api/services/api.service'
import { Profile } from '@ti/core/auth/services/my.profile'
import { Memoize } from '@ti/core/common/decorators/memoize'
import { completeStore } from '@ti/core/common/utils/complete-store'
import { injectDestroy$ } from '@ti/core/common/utils/inject-destroy-event'
import { toSignalState } from '@ti/core/common/utils/signal-state'

export abstract class BaseCustomSensorsProvider<Sensor extends CustomSensor = CustomSensor> {
  protected readonly api = inject(ApiService)
  protected readonly profile = inject(Profile)
  protected readonly injector = inject(Injector)
  protected readonly destroy$ = injectDestroy$()

  protected readonly store = {
    customSensors$: new BehaviorSubject<Sensor[]>([]),
    customSensorsById$: new BehaviorSubject<Record<string, Sensor>>({}),
  }

  @Memoize()
  public get state() {
    return toSignalState(this.state$, this.injector)
  }

  @Memoize()
  public get state$() {
    return {
      customSensors: this.store.customSensors$.asObservable(),
      customSensorsById: this.store.customSensorsById$.asObservable(),
    }
  }

  public constructor() {
    Promise.resolve()
      .then(() => this.init())
      .catch((e) => console.warn(`Cannot init CustomSensorsProvider`, e))
  }

  protected abstract getCustomSensors(): Promise<Sensor[]>

  public async loadCustomSensors() {
    // Temporary solution
    const profile = await this.profile.getData()
    if (!profile) return

    try {
      const customSensors = await this.getCustomSensors()
      this.store.customSensors$.next(customSensors)
      const customSensorsById = Object.fromEntries(
        customSensors.map((customSensor) => [customSensor.id, customSensor]),
      )
      this.store.customSensorsById$.next(customSensorsById)
    } catch (e) {
      console.error('Error to load custom sensors list', e)
    }
  }

  protected async init() {
    await this.loadCustomSensors()
    this.destroy$.subscribe(() => completeStore(this.store))
  }
}
